import * as yup from 'yup';
import { HOMEWORK_KIND_VALUES, homeworkTargetSchema } from './homework';

/**
 * E34 · Pathways — the both-ends input codecs of the pathway mutations (block 3a; plan 03 §2.1 #3): the
 * portal's Builder, the Enroll drawer, Release / Withdraw and the phone's Start / Leave validate with the
 * SAME schema before sending, the API runs it again as the shield's inputRule. SHAPE only — lengths, the
 * closed vocabularies, a release rule's own numbers, one id per step. The business rules (no steps, a first
 * step waiting for a previous one, a targeted step without its target, a session step in a public course,
 * the capabilities, the access rule) are the deciders', where they are binding (`@cc/domain`
 * homework/pathways.ts and the slices).
 *
 * The vocabularies below MUST equal `@cc/domain`'s (`PATHWAY_VISIBILITIES`, `PATHWAY_RELEASE_RULES`) —
 * `@cc/domain` depends on this package, so the lists live twice and a domain spec (`homework/specs/
 * wire.spec.ts`) pins them equal.
 *
 * ABSENT on purpose: `pathwayId` on Define (minted server-side), `practiceId`, `performer`, `requester`,
 * `version` (the server's), `enrollmentId` on Enroll (minted), `assignmentId` on Release (minted) — a caller
 * that could name them could write into another practice's library or another person's record.
 */
export const PATHWAY_VISIBILITY_VALUES = ['practice', 'public', 'prescribeOnly'] as const;
export const PATHWAY_RELEASE_RULE_VALUES = [
  'immediately',
  'onDay',
  'afterPrevious',
  'manual',
] as const;

const id = (max = 128) => yup.string().trim().min(1).max(max);
const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);

export const pathwayReleaseRuleSchema = yup.object({
  rule: yup
    .string()
    .oneOf([...PATHWAY_RELEASE_RULE_VALUES])
    .required(),
  // onDay: days after the start (a year at most); afterPrevious: days after the previous outcome
  day: yup
    .number()
    .integer()
    .min(0)
    .max(365)
    .nullable()
    .when('rule', {
      is: 'onDay',
      then: (s) => s.required(),
    }),
  delayDays: yup.number().integer().min(0).max(365).nullable(),
});

export const pathwayStepSchema = yup.object({
  stepId: id(64).required(),
  kind: yup
    .string()
    .oneOf([...HOMEWORK_KIND_VALUES])
    .required(),
  target: homeworkTargetSchema,
  title: yup.string().trim().min(1).max(200).required(),
  instructions: yup.string().trim().max(4000).nullable(),
  release: pathwayReleaseRuleSchema.required(),
  dueDays: yup.number().integer().min(0).max(365).nullable(),
});

export const pathwaySectionSchema = yup.object({
  section: yup.string().trim().min(1).max(200).required(),
  steps: yup.array().of(pathwayStepSchema.required()).max(100).required(),
});

/** One id per step across the pathway — the release, the progress and the gear key on it. */
const uniqueStepIds = (sections: unknown): boolean => {
  const ids = ((sections as Array<{ steps?: Array<{ stepId?: string }> }> | undefined) ?? [])
    .flatMap((s) => s.steps ?? [])
    .map((s) => s.stepId);
  return new Set(ids).size === ids.length;
};

const sections = yup
  .array()
  .of(pathwaySectionSchema.required())
  .max(60)
  .required()
  .test('unique-step-ids', 'two steps share an id', uniqueStepIds);

const body = {
  name: yup.string().trim().min(1).max(200).required(),
  description: yup.string().trim().max(2000).nullable(),
  visibility: yup
    .string()
    .oneOf([...PATHWAY_VISIBILITY_VALUES])
    .required(),
  sections,
};

export const definePathwayInputSchema = yup.object({
  ...body,
  copiedFrom: id().nullable(),
});

export const revisePathwayInputSchema = yup.object({
  pathwayId: id().required(),
  ...body,
});

export const retirePathwayInputSchema = yup.object({
  pathwayId: id().required(),
  reason: yup.string().trim().max(1000).nullable(),
});

export const enrollInPathwayInputSchema = yup.object({
  pathwayId: id().required(),
  // absent: the caller starts a course themself (the phone)
  subject: userRef.nullable(),
  startAt: yup.date().nullable(),
});

export const releasePathwayStepInputSchema = yup.object({
  enrollmentId: id().required(),
  stepId: id(64).required(),
});

export const withdrawFromPathwayInputSchema = yup.object({
  enrollmentId: id().required(),
  reason: yup.string().trim().max(1000).nullable(),
});

// ── block 5a (2026-09-29): the backoffice's course access, capabilities and review; the phone's purchase ──
// The vocabularies below MUST equal `@cc/domain`'s (`PATHWAY_ACCESS_RULES`, `PRACTICE_COURSE_CAPABILITIES`,
// `REVIEW_DECISIONS`) — pinned by `homework/specs/wire.spec.ts`. ABSENT on purpose: `performer` (the session's),
// `source` (the backoffice switch is `backoffice`, the plan reactor's `plan` never comes over the wire),
// `subject` on the purchase (the caller).
export const PATHWAY_ACCESS_RULE_VALUES = ['free', 'purchase', 'plan'] as const;
export const PRACTICE_CAPABILITY_VALUES = [
  'prescribeCourses',
  'customiseCourses',
  'publishCourses',
] as const;
export const REVIEW_DECISION_VALUES = ['approve', 'reject'] as const;

/**
 * A store product id as App Store Connect and the Google Play Console both accept it (letters, digits, dots
 * and underscores; starting with a letter or digit) — the same id in both stores and in Adapty.
 */
export const STORE_PRODUCT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._]{0,99}$/;
const storeProductId = yup.string().trim().matches(STORE_PRODUCT_ID_PATTERN);

export const setPathwayAccessRuleInputSchema = yup.object({
  pathwayId: id().required(),
  accessRule: yup
    .string()
    .oneOf([...PATHWAY_ACCESS_RULE_VALUES])
    .required(),
  // required with `purchase` — the decider's PATHWAY_ACCESS_PRODUCT_REQUIRED says so in the person's words
  storeProductId: storeProductId.nullable(),
  // Q-b5-1 B — the course's own Adapty placement (its paywall); same shape as the product id; required with purchase
  adaptyPlacementId: storeProductId.nullable(),
});

export const setPracticeCapabilityInputSchema = yup.object({
  practiceId: yup
    .string()
    .trim()
    .matches(/^practices\/[A-Za-z0-9_-]{1,64}$/)
    .required(),
  // an unknown name is the decider's PRACTICE_CAPABILITY_UNKNOWN (the board's refusal) — shape only here
  capability: id(64).required(),
  granted: yup.boolean().required(),
});

export const reviewPathwayPublicationInputSchema = yup.object({
  pathwayId: id().required(),
  version: yup.number().integer().min(1).required(),
  decision: yup
    .string()
    .oneOf([...REVIEW_DECISION_VALUES])
    .required(),
  // required with `reject` — the decider's PATHWAY_REVIEW_REASON_REQUIRED
  reason: yup.string().trim().max(2000).nullable(),
});

export const recordCoursePurchaseInputSchema = yup.object({
  vendorProductId: storeProductId.required(),
  adaptyProfileId: yup.string().trim().max(128).nullable(),
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
export const definePathwayArgsSchema = yup.object({
  input: definePathwayInputSchema.required(),
});
export const revisePathwayArgsSchema = yup.object({
  input: revisePathwayInputSchema.required(),
});
export const retirePathwayArgsSchema = yup.object({
  input: retirePathwayInputSchema.required(),
});
export const enrollInPathwayArgsSchema = yup.object({
  input: enrollInPathwayInputSchema.required(),
});
export const releasePathwayStepArgsSchema = yup.object({
  input: releasePathwayStepInputSchema.required(),
});
export const withdrawFromPathwayArgsSchema = yup.object({
  input: withdrawFromPathwayInputSchema.required(),
});

export const setPathwayAccessRuleArgsSchema = yup.object({
  input: setPathwayAccessRuleInputSchema.required(),
});
export const setPracticeCapabilityArgsSchema = yup.object({
  input: setPracticeCapabilityInputSchema.required(),
});
export const reviewPathwayPublicationArgsSchema = yup.object({
  input: reviewPathwayPublicationInputSchema.required(),
});
export const recordCoursePurchaseArgsSchema = yup.object({
  input: recordCoursePurchaseInputSchema.required(),
});

export const pathwayValidators = {
  setPathwayAccessRuleInputSchema,
  setPracticeCapabilityInputSchema,
  reviewPathwayPublicationInputSchema,
  recordCoursePurchaseInputSchema,
  setPathwayAccessRuleArgsSchema,
  setPracticeCapabilityArgsSchema,
  reviewPathwayPublicationArgsSchema,
  recordCoursePurchaseArgsSchema,
  definePathwayInputSchema,
  revisePathwayInputSchema,
  retirePathwayInputSchema,
  enrollInPathwayInputSchema,
  releasePathwayStepInputSchema,
  withdrawFromPathwayInputSchema,
  definePathwayArgsSchema,
  revisePathwayArgsSchema,
  retirePathwayArgsSchema,
  enrollInPathwayArgsSchema,
  releasePathwayStepArgsSchema,
  withdrawFromPathwayArgsSchema,
};
